import { getCurrentFormatterLocale } from './localeFormatting';

function normalizeDateInput(value) {
  if (typeof value !== 'string') return value;

  const trimmedValue = value.trim();
  const hasDateTime = /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}/.test(trimmedValue);
  const hasExplicitTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmedValue);

  if (!hasDateTime || hasExplicitTimezone) return value;

  return `${trimmedValue.replace(' ', 'T')}Z`;
}

function toDate(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const dateValue = new Date(normalizeDateInput(value));
  return Number.isNaN(dateValue.getTime()) ? null : dateValue;
}

export const parseDateTimeMs = value => {
  const dateValue = toDate(value);
  return dateValue ? dateValue.getTime() : null;
};

function resolveFormatterLocale(language, fallback = 'en') {
  return getCurrentFormatterLocale(language, fallback);
}

export const getDatePickerFormat = (language, { showTime = false } = {}) => {
  const parts = new Intl.DateTimeFormat(resolveFormatterLocale(language), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    calendar: 'gregory',
    numberingSystem: 'latn',
  }).formatToParts(new Date(2000, 10, 22));
  const tokens = { year: 'YYYY', month: 'MM', day: 'DD' };
  const dateFormat = parts.map(({ type, value }) => {
    if (tokens[type]) return tokens[type];

    // Typed dates must not require invisible direction marks from Intl literals.
    const literal = value.replace(/[\u061c\u200e\u200f]/g, '');
    return literal ? `[${literal}]` : '';
  }).join('');

  return showTime ? `${dateFormat} HH:mm:ss` : dateFormat;
};

function hasDateTimeComponentOptions(formatOptions = {}) {
  return [
    'weekday',
    'era',
    'year',
    'month',
    'day',
    'dayPeriod',
    'hour',
    'minute',
    'second',
    'fractionalSecondDigits',
    'timeZoneName',
  ].some(option => Object.prototype.hasOwnProperty.call(formatOptions, option));
}

function formatWithDateTimeOptions(value, options = {}, fallback = '') {
  const dateValue = toDate(value);
  if (!dateValue) return fallback;

  const locale = resolveFormatterLocale(options.language || options.locale, options.localeFallback || 'en');

  return new Intl.DateTimeFormat(locale, options.formatOptions).format(dateValue);
}

export const formatDateTime = (value, options = {}) => {
  const shouldUseDefaultStyles = !hasDateTimeComponentOptions(options.formatOptions);

  return formatWithDateTimeOptions(value, {
    ...options,
    formatOptions: {
      ...(shouldUseDefaultStyles ? {
        dateStyle: options.dateStyle || 'medium',
        timeStyle: options.timeStyle || 'short',
      } : {}),
      ...options.formatOptions,
    },
  }, options.fallback ?? '');
};

export const formatDate = (value, options = {}) => {
  return formatDateTime(value, options);
};

export const formatDateOnly = (value, options = {}) => {
  const shouldUseDefaultDateStyle = !hasDateTimeComponentOptions(options.formatOptions);

  return formatWithDateTimeOptions(value, {
    ...options,
    formatOptions: {
      ...(shouldUseDefaultDateStyle ? { dateStyle: options.dateStyle || 'medium' } : {}),
      ...options.formatOptions,
    },
  }, options.fallback ?? '');
};

export const formatTimestamp = (value, fallback = '', options = {}) => {
  if (!value) return fallback;

  const dateValue = typeof value === 'number' ? value * 1000 : value;

  return formatDateTime(dateValue, { ...options, fallback }) || fallback;
};

export const formatTime = (value, options = {}) => {
  const shouldUseDefaultTimeStyle = !hasDateTimeComponentOptions(options.formatOptions);

  return formatWithDateTimeOptions(value, {
    ...options,
    formatOptions: {
      ...(shouldUseDefaultTimeStyle ? { timeStyle: options.timeStyle || 'short' } : {}),
      ...options.formatOptions,
    },
  }, options.fallback ?? '');
};

export const formatRelativeTime = (value, options = {}) => {
  if (value === null || value === undefined || value === '') return options.fallback ?? null;

  const numericTimestamp = Number(value);
  const timestamp = value instanceof Date
    ? value.getTime()
    : Number.isFinite(numericTimestamp)
      ? numericTimestamp
      : parseDateTimeMs(value);
  if (!Number.isFinite(timestamp)) return options.fallback ?? null;

  const now = options.now ?? Date.now();
  const diffInMs = timestamp - now;
  const absDiffInMs = Math.abs(diffInMs);
  const locale = resolveFormatterLocale(options.language || options.locale, options.localeFallback || 'en');
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: options.numeric || 'auto' });

  if (absDiffInMs < 60_000) return formatter.format(0, 'minute');
  if (absDiffInMs < 3_600_000) return formatter.format(Math.round(diffInMs / 60_000), 'minute');
  if (absDiffInMs < 86_400_000) return formatter.format(Math.round(diffInMs / 3_600_000), 'hour');
  if (absDiffInMs < 604_800_000) return formatter.format(Math.round(diffInMs / 86_400_000), 'day');
  if (absDiffInMs < 2_592_000_000) return formatter.format(Math.round(diffInMs / 604_800_000), 'week');
  if (absDiffInMs < 31_536_000_000) return formatter.format(Math.round(diffInMs / 2_592_000_000), 'month');
  return formatter.format(Math.round(diffInMs / 31_536_000_000), 'year');
};

export const getTimeAgo = (timestamp, locale) => {
  return formatRelativeTime(timestamp, { language: locale });
};
